import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import {
  UserAdmin,
  UserAdminFilter,
  UsersAdminResult,
  UpdateUserRequest,
  OfficeOption
} from '../models/user-administrators.model';

@Injectable({
  providedIn: 'root'
})
export class UserAdministratorsService {

  private readonly endpoint = 'User';
  private readonly officesEndpoint = 'offices';
  private readonly roleName = 'User Admin';

  constructor(
    private http: HttpClient,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private clientSelectionService: ClientSelectionService
  ) {}

  getUsers(
    clientId: number,
    skip = 0,
    count = 10,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filter?: UserAdminFilter
  ): Observable<UsersAdminResult> {
    let params = new HttpParams()
      .set('clientId', clientId.toString())
      .set('roleName', this.roleName)
      .set('skip', skip.toString())
      .set('count', count.toString());

    if (filter?.firstName?.trim()) params = params.set('firstName', filter.firstName.trim());
    if (filter?.lastName?.trim())  params = params.set('lastName',  filter.lastName.trim());
    if (filter?.email?.trim())     params = params.set('email',     filter.email.trim());
    if (filter?.userName?.trim())  params = params.set('userName',  filter.userName.trim());

    if (sortBy) params = params.set('sortBy', sortBy);
    if (sortDir) params = params.set('sortDir', sortDir);

    return this.http
      .get<any>(`${environment.apiUrl}${this.endpoint}`, { headers: this.getHeaders(), params })
      .pipe(
        map(response => ({
          users: (response?.users ?? []).map((u: any) => this.mapUser(u)),
          totalCount: response?.totalCount ?? 0
        }))
      );
  }

  updateUser(request: UpdateUserRequest): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${environment.apiUrl}${this.endpoint}`,
      request,
      { headers: this.getHeaders() }
    );
  }

  getOffices(): Observable<OfficeOption[]> {
    return this.http
      .get<any>(`${environment.apiUrl}${this.officesEndpoint}`, { headers: this.getHeaders() })
      .pipe(
        map((res: any) => {
          const list: any[] = this.extractOffices(res);
          return list.map((o: any) => this.mapOffice(o));
        })
      );
  }

  private mapUser(u: any): UserAdmin {
    const officeIdRaw: string = u.OfficeID ?? u.officeID ?? u.officeIds ?? '';
    const officeIds: number[] = typeof officeIdRaw === 'string'
      ? officeIdRaw.split(';').map(Number).filter(id => !isNaN(id) && id > 0)
      : Array.isArray(officeIdRaw) ? officeIdRaw : [];
    return {
      userName:  u.UserName  ?? u.userName  ?? '',
      firstName: u.FirstName ?? u.firstName ?? '',
      lastName:  u.LastName  ?? u.lastName  ?? '',
      email:     u.Email     ?? u.email     ?? '',
      office:    u.Office    ?? u.office    ?? '',
      officeIds,
      locked:   u.Locked   ?? u.locked   ?? false,
      approved: u.Approved ?? u.approved ?? false,
      newUser:  u.NewUser  ?? u.newUser  ?? false
    };
  }

  private mapOffice(o: any): OfficeOption {
    return {
      claimOfficeId: Number(o.ClaimOfficeID ?? o.claimOfficeID ?? o.claimOfficeId ?? 0),
      mailingName: String(o.MailingName ?? o.mailingName ?? o.OfficeName ?? o.officeName ?? '')
    };
  }

  private extractOffices(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.offices)) {
      return response.offices;
    }

    if (Array.isArray(response?.Offices)) {
      return response.Offices;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  private getHeaders(): HttpHeaders {
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();
    const token = this.oktaAuth.getAccessToken();
    if (!token) throw new Error('No access token found');
    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${token}`,
      ClientDBName: clientDbName
    });
  }
}
